import { SlotInterface, SlotsOverview } from 'types';
import HTTPClient, { HTTPClientParameters } from './HTTPClient';
import { PagedResponse } from 'types/Pagination';
import { RentalType, SlotState } from 'utils/constants';
import cache from './cache';
import { buildQueryParams } from 'utils/helpers';

export const SLOT_PAGE_SIZE = 20;

type SlotClientParams = HTTPClientParameters;

export type PaginatedRentals = PagedResponse<SlotInterface>;

// On-behalf-of edits (owner editing a renter's slot) must send plate data, not
// pre-created car PKs. The backend owns the resulting cars to slot.renter. EXP-955.
type UpdateSlotWithCarsListParams = {
    cars_list: { plate: string; state: string; country: string }[];
};

export type AddSlotParams = {
    first_name: string;
    last_name: string;
    cars: { plate: string; state: string; country: string }[];
    email: string;
};

type AddSlotPayload = AddSlotParams & { spot: string };

type GetSlotPayload = {
    rentalType: RentalType;
    spotId: number;
    state?: SlotState;
    page?: number;
    searchTerm?: string;
};

export enum BulkRentalSlotsFilter {
    NotEnded = 'NOT_ENDED',
    Ended = 'ENDED',
}
const BulkRentalSlotsFilterMap: Record<BulkRentalSlotsFilter, string> = { ENDED: 'true', NOT_ENDED: 'false' };

type QueryBulkRentalsParams = {
    bulkRentalUuid: string;
    page?: number;
    pageSize?: number;
    searchTerm?: string;
    filter: BulkRentalSlotsFilter;
    ordering?: string;
};

class SlotClient extends HTTPClient {
    constructor(params: SlotClientParams) {
        super(params);
        this.headers.set('Content-Type', 'application/json');
    }

    updateCarsListOfRental(uuid: string, params: UpdateSlotWithCarsListParams): Promise<SlotInterface> {
        return this.patch(`slots/${uuid}/`, params);
    }

    deleteSlot(uuid: string): Promise<SlotInterface | undefined> {
        return this.delete(`slots/${uuid}/`);
    }

    addSlot(payload: AddSlotPayload): Promise<SlotInterface> {
        return this.post(`managed_monthly_slots/`, payload);
    }

    _getSlots({
        rentalType,
        spotId,
        page = 1,
        state = SlotState.Active,
        searchTerm,
    }: GetSlotPayload): Promise<PaginatedRentals> {
        const params = new URLSearchParams();
        params.set('ended', 'false');
        if (state === SlotState.Active) params.set('ordering', '-start_date');
        params.set('page_size', SLOT_PAGE_SIZE.toString());
        params.set('rental_type__in', rentalType);
        params.set('spot', spotId.toString());
        params.set('page', page.toString());
        params.set('state', state);
        if (searchTerm) {
            params.set('search', searchTerm);
        }
        return this.get(`slots?${params.toString()}`);
    }

    @cache(1 * 60000) // 1min of cache
    getSlots(payload: GetSlotPayload): Promise<PaginatedRentals> {
        return this._getSlots(payload);
    }

    @cache(0)
    force__getSlots(payload: GetSlotPayload): Promise<PaginatedRentals> {
        return this._getSlots(payload);
    }

    @cache(5 * 60000)
    getSlotOverview(spotId: string, startDateFrom: number, startDateTo: number): Promise<SlotsOverview> {
        return this.get(`slots/overview?spot=${spotId}&start_date_from=${startDateFrom}&start_date_to=${startDateTo}`);
    }

    queryBulkRentals({
        bulkRentalUuid: bulk_rental,
        page = 1,
        pageSize = SLOT_PAGE_SIZE,
        searchTerm: search,
        filter,
        ordering = '-start_date',
    }: QueryBulkRentalsParams): Promise<PaginatedRentals> {
        const ended = BulkRentalSlotsFilterMap[filter];
        return this.get(`slots${buildQueryParams({ bulk_rental, page, pageSize, search, ordering, ended })}`);
    }
}

export default SlotClient;
